import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaShieldAlt, FaArrowLeft } from 'react-icons/fa';
import PageTransition from '../components/PageTransition';
import { fadeInUp, staggerContainer } from '../utils/animations';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-black text-white mb-4 flex items-center gap-2">
      <span className="w-1 h-5 bg-white rounded-full inline-block" />
      {title}
    </h2>
    <div className="text-gray-400 leading-relaxed space-y-3 text-sm">{children}</div>
  </div>
);

const Privacy = () => (
  <PageTransition>
    <div className="bg-transparent min-h-screen">
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-3xl">

          <motion.div variants={fadeInUp} initial="hidden" animate="visible" className="mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm font-semibold transition-colors duration-200 group">
              <FaArrowLeft className="text-xs group-hover:-translate-x-0.5 transition-transform duration-200" />
              Back to Home
            </Link>
          </motion.div>

          <motion.div variants={staggerContainer} initial="hidden" animate="visible">
            <motion.div variants={fadeInUp} className="inline-flex mb-6">
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-900/60 border border-gray-700 rounded-full">
                <FaShieldAlt className="text-gray-400 text-xs" />
                <span className="text-xs font-semibold tracking-widest text-gray-300 uppercase">Legal</span>
              </div>
            </motion.div>

            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl font-black text-white mb-4">
              Privacy Policy
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-gray-500 text-sm mb-12">
              Last updated: March 2026
            </motion.p>

            <motion.div variants={fadeInUp} className="glass-card rounded-2xl p-8 md:p-10 space-y-2">
              <Section title="Introduction">
                <p>Zynexis ("we", "our", or "us") is committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website or engage our services.</p>
                <p>By using our services, you agree to the collection and use of information in accordance with this policy.</p>
              </Section>

              <Section title="Information We Collect">
                <p>We may collect the following types of information:</p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li><strong className="text-gray-300">Contact Information:</strong> Name, email address, phone number, and company name when you fill out our contact or application forms.</li>
                  <li><strong className="text-gray-300">Usage Data:</strong> Information about how you interact with our website, including pages visited, time spent, and referring URLs.</li>
                  <li><strong className="text-gray-300">Communications:</strong> Records of correspondence if you contact us directly.</li>
                  <li><strong className="text-gray-300">Technical Data:</strong> IP address, browser type, device identifiers, and operating system.</li>
                </ul>
              </Section>

              <Section title="How We Use Your Information">
                <p>We use the information we collect to:</p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li>Respond to your enquiries and provide requested services</li>
                  <li>Process job applications and evaluate candidate suitability</li>
                  <li>Improve and personalise your experience on our website</li>
                  <li>Send administrative information such as project updates</li>
                  <li>Comply with legal obligations and protect our legal rights</li>
                  <li>Analyse usage trends to improve our website and services</li>
                </ul>
              </Section>

              <Section title="How We Share Your Information">
                <p>We do not sell, trade, or rent your personal information to third parties. We may share information with:</p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li><strong className="text-gray-300">Service Providers:</strong> Trusted third-party vendors who assist us in operating our website and conducting our business (e.g. hosting, analytics, email delivery), subject to confidentiality agreements.</li>
                  <li><strong className="text-gray-300">Legal Requirements:</strong> When required by law or to protect the rights and safety of Zynexis, our clients, or the public.</li>
                </ul>
              </Section>

              <Section title="Data Retention">
                <p>We retain personal data only for as long as necessary to fulfil the purposes outlined in this policy, or as required by applicable law. Contact form submissions are retained for up to 2 years. Job application data is retained for 12 months after the position is filled.</p>
              </Section>

              <Section title="Cookies">
                <p>Our website uses cookies to enhance your browsing experience. Cookies are small text files stored on your device. We use:</p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li><strong className="text-gray-300">Essential Cookies:</strong> Required for the website to function correctly.</li>
                  <li><strong className="text-gray-300">Analytics Cookies:</strong> Help us understand how visitors interact with the website (e.g. Google Analytics).</li>
                </ul>
                <p className="mt-2">You can disable cookies through your browser settings, though this may affect your experience.</p>
              </Section>

              <Section title="Your Rights">
                <p>Depending on your location, you may have the right to:</p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li>Access the personal data we hold about you</li>
                  <li>Request correction of inaccurate data</li>
                  <li>Request deletion of your data ("right to be forgotten")</li>
                  <li>Object to or restrict our processing of your data</li>
                  <li>Request portability of your data</li>
                </ul>
                <p className="mt-2">To exercise any of these rights, please contact us at <a href="mailto:contact@zynexis.com" className="text-white hover:underline">contact@zynexis.com</a>.</p>
              </Section>

              <Section title="Security">
                <p>We implement industry-standard security measures to protect your personal information, including encryption in transit (TLS/HTTPS), access controls, and regular security reviews. However, no method of transmission over the Internet is 100% secure.</p>
              </Section>

              <Section title="Third-Party Links">
                <p>Our website may contain links to third-party websites. We are not responsible for the privacy practices of those sites and encourage you to review their privacy policies independently.</p>
              </Section>

              <Section title="Changes to This Policy">
                <p>We may update this Privacy Policy from time to time. We will notify you of significant changes by updating the "Last updated" date at the top. Continued use of our services after changes constitutes acceptance of the revised policy.</p>
              </Section>

              <Section title="Contact Us">
                <p>If you have questions or concerns about this Privacy Policy, please contact us:</p>
                <div className="mt-3 p-4 bg-gray-900/60 rounded-xl border border-gray-700 space-y-1">
                  <p><strong className="text-gray-300">Zynexis</strong></p>
                  <p>Email: <a href="mailto:contact@zynexis.com" className="text-white hover:underline">contact@Zynexis.com</a></p>
                </div>
              </Section>
            </motion.div>
          </motion.div>

        </div>
      </section>
    </div>
  </PageTransition>
);

export default Privacy;
